'use client';

import { useTranslations } from 'next-intl';

import { cn } from '@/lib/utils';
import type { BaseModalProps } from '@/types/modal';

import { SpinnerLoader } from '../Loading';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

// Add a VisuallyHidden component for accessibility
function VisuallyHidden({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="absolute h-px w-px overflow-hidden whitespace-nowrap border-0 p-0"
      style={{
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        margin: '-1px',
      }}
    >
      {children}
    </span>
  );
}

function BaseModal(props: BaseModalProps) {
  const {
    onOpenChange,
    open,
    defaultOpen,
    title,
    description,
    children,
    footer,
    className,
    fullscreen = false,
    isLoadingContent = false,
  } = props;

  const translate = useTranslations();

  const renderFooter = () => {
    if (footer === undefined) {
      return null;
    }
    if (footer === null) {
      return (
        <DialogFooter className="items-end sm:justify-start">
          <DialogClose asChild>
            <Button
              onClick={() => {
                onOpenChange(false);
              }}
              type="button"
              variant="secondary"
            >
              {translate('modal.close')}
            </Button>
          </DialogClose>
        </DialogFooter>
      );
    }
    return (
      <DialogFooter className="items-end sm:justify-start">
        {footer}
      </DialogFooter>
    );
  };

  return (
    <Dialog defaultOpen={defaultOpen} open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          fullscreen
            ? 'h-screen w-screen flex flex-col'
            : 'max-h-[90vh] min-h-min bg-white sm:max-w-4xl flex flex-col',
          className,
        )}
      >
        <DialogHeader className="flex items-start justify-center gap-4">
          {title
            ? (
                <DialogTitle>{title}</DialogTitle>
              )
            : (
                <VisuallyHidden>
                  <DialogTitle>{translate('modal.modalTitle')}</DialogTitle>
                </VisuallyHidden>
              )}
          {description && (
            <DialogDescription className="text-gray-600">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          {isLoadingContent && (
            <div className="my-2 flex w-full items-center justify-center">
              <SpinnerLoader size="xl" className="text-primary" />
            </div>
          )}
          {isLoadingContent === false && children}
        </div>
        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
}

export default BaseModal;
